import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import "leaflet.heat";
import { MockProperty } from "@/data/mockProperties";
import { calculateSection8DealScore } from "@/lib/section8Scoring";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ZoomIn, ZoomOut, Locate, Layers, Flame } from "lucide-react";

// Extend L namespace for heat layer
declare module "leaflet" {
  function heatLayer(
    latlngs: Array<[number, number, number?]>,
    options?: {
      minOpacity?: number;
      maxZoom?: number;
      max?: number;
      radius?: number;
      blur?: number;
      gradient?: Record<number, string>;
    },
  ): L.Layer;
}

interface InteractivePropertyMapProps {
  properties: MockProperty[];
  selectedProperty?: MockProperty | null;
  onPropertySelect?: (property: MockProperty) => void;
  className?: string;
}

function getScoreColor(score: number): string {
  if (score >= 80) return "#22c55e";
  if (score >= 70) return "#84cc16";
  if (score >= 60) return "#eab308";
  if (score >= 50) return "#f97316";
  return "#ef4444";
}

function getScoreGrade(score: number): string {
  if (score >= 90) return "A+";
  if (score >= 85) return "A";
  if (score >= 80) return "A-";
  if (score >= 75) return "B+";
  if (score >= 70) return "B";
  if (score >= 65) return "B-";
  if (score >= 60) return "C+";
  if (score >= 55) return "C";
  if (score >= 50) return "C-";
  if (score >= 45) return "D+";
  if (score >= 40) return "D";
  return "F";
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/** Secondary + frosted bg keeps secondary-foreground (light); force readable icons on bg-background/90 in light mode */
const MAP_TOOLBAR_BUTTON_CLASS =
  "border border-border/50 bg-background/90 text-foreground backdrop-blur-sm shadow-lg hover:bg-muted hover:text-foreground";

// Custom marker icon creator
function createMarkerIcon(
  price: number,
  grade: string,
  color: string,
  isSelected: boolean,
) {
  const size = isSelected ? 1.2 : 1;
  const html = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      transform: scale(${size});
      transition: transform 0.2s ease-out;
      filter: ${isSelected ? "drop-shadow(0 4px 12px rgba(0,0,0,0.4))" : "drop-shadow(0 2px 6px rgba(0,0,0,0.3))"};
    ">
      <div style="
        background: ${color};
        color: white;
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 700;
        font-family: system-ui, -apple-system, sans-serif;
        white-space: nowrap;
        margin-bottom: 4px;
      ">${formatCurrency(price)}</div>
      <div style="
        width: 26px;
        height: 26px;
        background: ${color};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 10px;
        font-weight: 700;
        font-family: system-ui, -apple-system, sans-serif;
        border: 2px solid white;
      ">${grade}</div>
      <div style="
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 8px solid ${color};
      "></div>
    </div>
  `;

  return L.divIcon({
    html,
    className: "custom-marker",
    iconSize: [60, 60],
    iconAnchor: [30, 60],
    popupAnchor: [0, -60],
  });
}

// Popup content generator
function createPopupContent(
  property: MockProperty,
  score: number,
  color: string,
  grade: string,
): string {
  return `
    <div style="min-width: 200px; padding: 4px;">
      <img 
        src="${property.imageUrl}" 
        alt="${property.address}"
        style="width: 100%; height: 96px; object-fit: cover; border-radius: 6px; margin-bottom: 8px;"
      />
      <p style="font-weight: bold; font-size: 18px; margin: 0 0 4px 0;">${formatCurrency(property.price)}</p>
      <p style="font-size: 14px; color: #666; margin: 0 0 2px 0;">${property.address}</p>
      <p style="font-size: 12px; color: #888; margin: 0 0 8px 0;">${property.city}, ${property.state} ${property.zip}</p>
      <div style="display: flex; gap: 12px; font-size: 12px; color: #666; margin-bottom: 8px;">
        <span>🛏️ ${property.bedrooms} bd</span>
        <span>🛁 ${property.bathrooms} ba</span>
        <span>📐 ${property.sqft.toLocaleString()} sqft</span>
      </div>
      <div style="display: flex; gap: 6px;">
        <span style="background: ${color}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">${grade} Grade</span>
        <span style="background: #f1f5f9; color: #334155; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">${property.capRate}% Cap</span>
      </div>
    </div>
  `;
}

// Marker cluster layer component
function MarkerClusterLayer({
  properties,
  propertyScores,
  selectedProperty,
  onPropertySelect,
}: {
  properties: MockProperty[];
  propertyScores: Map<string, number>;
  selectedProperty?: MockProperty | null;
  onPropertySelect?: (property: MockProperty) => void;
}) {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    // Remove existing cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    // Create new cluster group with custom styling
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 16,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const markers = cluster.getAllChildMarkers();

        // Calculate average score for cluster color
        let totalScore = 0;
        markers.forEach((marker: any) => {
          totalScore += marker.options.score || 0;
        });
        const avgScore = totalScore / markers.length;
        const color = getScoreColor(avgScore);

        // Calculate total value
        let totalValue = 0;
        markers.forEach((marker: any) => {
          totalValue += marker.options.price || 0;
        });
        const avgValue = Math.round(totalValue / markers.length);

        // Size based on count
        let size = 40;
        let fontSize = 12;
        if (count >= 100) {
          size = 55;
          fontSize = 14;
        } else if (count >= 50) {
          size = 50;
          fontSize = 13;
        } else if (count >= 10) {
          size = 45;
          fontSize = 12;
        }

        return L.divIcon({
          html: `
            <div style="
              display: flex;
              flex-direction: column;
              align-items: center;
              filter: drop-shadow(0 3px 8px rgba(0,0,0,0.3));
            ">
              <div style="
                width: ${size}px;
                height: ${size}px;
                background: ${color};
                border-radius: 50%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                color: white;
                font-family: system-ui, -apple-system, sans-serif;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              ">
                <span style="font-size: ${fontSize}px; font-weight: 700; line-height: 1;">${count}</span>
                <span style="font-size: 9px; opacity: 0.9;">properties</span>
              </div>
              <div style="
                margin-top: 4px;
                background: rgba(0,0,0,0.75);
                color: white;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
                font-weight: 600;
                font-family: system-ui, -apple-system, sans-serif;
              ">avg ${formatCurrency(avgValue)}</div>
            </div>
          `,
          className: "custom-cluster-marker",
          iconSize: L.point(size + 20, size + 30),
          iconAnchor: L.point((size + 20) / 2, (size + 30) / 2),
        });
      },
    });

    // Add markers to cluster
    const validProperties = properties.filter((p) => p.lat && p.lng);
    validProperties.forEach((property) => {
      const score = propertyScores.get(property.id) || 0;
      const color = getScoreColor(score);
      const grade = getScoreGrade(score);
      const isSelected = selectedProperty?.id === property.id;

      const marker = L.marker([property.lat, property.lng], {
        icon: createMarkerIcon(property.price, grade, color, isSelected),
        score,
        price: property.price,
      } as any);

      marker.bindPopup(createPopupContent(property, score, color, grade), {
        maxWidth: 280,
        className: "property-popup",
      });

      marker.on("click", () => {
        onPropertySelect?.(property);
      });

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, properties, propertyScores, selectedProperty, onPropertySelect]);

  return null;
}

// Map controller component for external controls
function MapController({
  properties,
  selectedProperty,
  onFitAll,
}: {
  properties: MockProperty[];
  selectedProperty?: MockProperty | null;
  onFitAll: (fn: () => void) => void;
}) {
  const map = useMap();

  // Register fit all callback
  useEffect(() => {
    onFitAll(() => {
      const validProps = properties.filter((p) => p.lat && p.lng);
      if (validProps.length === 0) return;

      const bounds = L.latLngBounds(validProps.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    });
  }, [map, properties, onFitAll]);

  // Fly to selected property
  useEffect(() => {
    if (selectedProperty?.lat && selectedProperty?.lng) {
      map.flyTo(
        [selectedProperty.lat, selectedProperty.lng],
        Math.max(map.getZoom(), 14),
        {
          duration: 0.8,
        },
      );
    }
  }, [selectedProperty, map]);

  // Fit bounds on initial load
  useEffect(() => {
    const validProps = properties.filter((p) => p.lat && p.lng);
    if (validProps.length === 0) return;

    const bounds = L.latLngBounds(validProps.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
  }, [properties.length > 0]); // Only run once when properties first load

  return null;
}

// Heat map layer component
function HeatmapLayer({
  properties,
  propertyScores,
  visible,
}: {
  properties: MockProperty[];
  propertyScores: Map<string, number>;
  visible: boolean;
}) {
  const map = useMap();
  const heatLayerRef = useRef<L.Layer | null>(null);

  useEffect(() => {
    // Remove existing heat layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }

    if (!visible) return;

    // Create heat data - only include investment-grade properties (score >= 60)
    const validProperties = properties.filter((p) => p.lat && p.lng);
    const heatData: Array<[number, number, number]> = validProperties
      .map((property) => {
        const score = propertyScores.get(property.id) || 0;
        // Weight by score - higher scores = more intensity
        // Only include properties with score >= 50 (investable)
        if (score < 50) return null;
        // Normalize intensity: score 50 = 0.3, score 100 = 1.0
        const intensity = 0.3 + ((score - 50) / 50) * 0.7;
        return [property.lat, property.lng, intensity] as [
          number,
          number,
          number,
        ];
      })
      .filter((item): item is [number, number, number] => item !== null);

    if (heatData.length === 0) return;

    // Create heat layer with investment-focused gradient
    const heatLayer = L.heatLayer(heatData, {
      radius: 35,
      blur: 25,
      maxZoom: 14,
      max: 1.0,
      minOpacity: 0.4,
      gradient: {
        0.0: "#3b82f6", // Blue - lower scores
        0.3: "#22c55e", // Green - good scores
        0.5: "#84cc16", // Lime - better scores
        0.7: "#eab308", // Yellow - high concentration
        0.85: "#f97316", // Orange - very high
        1.0: "#ef4444", // Red - hottest areas
      },
    });

    heatLayer.addTo(map);
    heatLayerRef.current = heatLayer;

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [map, properties, propertyScores, visible]);

  return null;
}

const tileLayerUrls = {
  light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  satellite:
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
};

const tileLayerAttribution = {
  light:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  dark: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  satellite: '&copy; <a href="https://www.esri.com">Esri</a>',
};

export function InteractivePropertyMap({
  properties,
  selectedProperty,
  onPropertySelect,
  className,
}: InteractivePropertyMapProps) {
  const [mapStyle, setMapStyle] = useState<"light" | "dark" | "satellite">(
    "light",
  );
  const [showHeatmap, setShowHeatmap] = useState(false);
  const fitAllRef = useRef<(() => void) | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  // Calculate scores
  const propertyScores = useMemo(() => {
    const scores = new Map<string, number>();
    properties.forEach((property) => {
      const score = calculateSection8DealScore(property);
      scores.set(property.id, score.percentage);
    });
    return scores;
  }, [properties]);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleFitAll = () => fitAllRef.current?.();
  const cycleMapStyle = () => {
    const styles: Array<"light" | "dark" | "satellite"> = [
      "light",
      "dark",
      "satellite",
    ];
    const currentIndex = styles.indexOf(mapStyle);
    setMapStyle(styles[(currentIndex + 1) % styles.length]);
  };

  // Default center on Birmingham, AL
  const defaultCenter: [number, number] = [33.5207, -86.8025];
  const defaultZoom = 11;

  return (
    <div className={cn("relative w-full h-full", className)}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        className="w-full h-full rounded-lg z-0"
        zoomControl={false}
        ref={mapRef}
      >
        <TileLayer
          key={mapStyle}
          url={tileLayerUrls[mapStyle]}
          attribution={tileLayerAttribution[mapStyle]}
        />

        <MapController
          properties={properties}
          selectedProperty={selectedProperty}
          onFitAll={(fn) => {
            fitAllRef.current = fn;
          }}
        />

        <MarkerClusterLayer
          properties={properties}
          propertyScores={propertyScores}
          selectedProperty={selectedProperty}
          onPropertySelect={onPropertySelect}
        />

        <HeatmapLayer
          properties={properties}
          propertyScores={propertyScores}
          visible={showHeatmap}
        />
      </MapContainer>

      {/* Custom Controls */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-[1000]">
        <Button
          size="icon"
          variant="secondary"
          className={MAP_TOOLBAR_BUTTON_CLASS}
          onClick={handleZoomIn}
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className={MAP_TOOLBAR_BUTTON_CLASS}
          onClick={handleZoomOut}
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className={MAP_TOOLBAR_BUTTON_CLASS}
          onClick={handleFitAll}
          title="Fit all"
        >
          <Locate className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className={MAP_TOOLBAR_BUTTON_CLASS}
          onClick={cycleMapStyle}
          title={`Style: ${mapStyle}`}
        >
          <Layers className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant={showHeatmap ? "default" : "secondary"}
          className={cn(
            "shadow-lg",
            showHeatmap
              ? "border-transparent bg-primary hover:bg-primary/90"
              : MAP_TOOLBAR_BUTTON_CLASS,
          )}
          onClick={() => setShowHeatmap(!showHeatmap)}
          title="Toggle heat map"
        >
          <Flame className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 shadow-lg z-[1000]">
        <p className="text-xs font-semibold mb-2">
          {showHeatmap ? "Investment Heat Map" : "Section 8 Deal Score"}
        </p>
        {showHeatmap ? (
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-3 rounded"
                style={{
                  background:
                    "linear-gradient(90deg, #3b82f6, #22c55e, #84cc16, #eab308, #f97316, #ef4444)",
                }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Low</span>
              <span>High</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Concentration of
              <br />
              investment-grade properties
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#22c55e" }}
              />
              <span>A (80+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#84cc16" }}
              />
              <span>B (70-79)</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#eab308" }}
              />
              <span>C (60-69)</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#f97316" }}
              />
              <span>D (50-59)</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#ef4444" }}
              />
              <span>F (&lt;50)</span>
            </div>
          </div>
        )}
      </div>

      {/* Property Count */}
      <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg z-[1000]">
        <p className="text-sm font-medium">{properties.length} properties</p>
      </div>

      {/* No Properties */}
      {properties.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg">
            <p className="text-muted-foreground">
              Search for properties to see them on the map
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
